import { cloneRgba, type Rgba } from '../color.ts';
import { pointInSolid } from '../collision.ts';
import { clampToBounds, clonePoint, distanceSquared } from './document.ts';
import { pointInPolygon } from './islands.ts';
import { DEFAULT_PARK_AREA_COLOR } from './palette.ts';
import {
  clearEdgeBend,
  findBendHandleAt,
  findRingEdgeAt,
  moveBendHandle,
  moveRingVertex,
  removeRingVertex,
  setCornerRadius,
  setEdgeBend,
  tessellateRing,
} from './ring.ts';
import {
  DEFAULT_CORNER_RADIUS,
  MIN_RING_VERTICES,
  type MapDocument,
  type ParkArea,
  type Vec2,
} from './types.ts';

/** Replaces any existing park area with a new one built from these vertices. */
export function setParkArea(map: MapDocument, vertices: readonly Vec2[]): ParkArea {
  if (vertices.length < MIN_RING_VERTICES) {
    throw new Error(`The park area needs at least ${MIN_RING_VERTICES} vertices`);
  }

  const area: ParkArea = {
    id: 'park-area',
    color: cloneRgba(map.parkArea?.color ?? DEFAULT_PARK_AREA_COLOR),
    vertices: vertices.map((vertex) => clampToBounds(clonePoint(vertex), map.bounds)),
    edges: vertices.map(() => ({ bend: null })),
    cornerRadius: DEFAULT_CORNER_RADIUS,
  };

  map.parkArea = area;
  return area;
}

/** Vertex average — enough to aim finished boats into the ring. */
export function parkAreaCentroid(map: MapDocument): Vec2 | null {
  const vertices = map.parkArea?.vertices;

  if (!vertices || vertices.length === 0) {
    return null;
  }

  const total = vertices.reduce((acc, vertex) => ({ x: acc.x + vertex.x, y: acc.y + vertex.y }), {
    x: 0,
    y: 0,
  });

  return { x: total.x / vertices.length, y: total.y / vertices.length };
}

export function clearParkArea(map: MapDocument): boolean {
  if (!map.parkArea) {
    return false;
  }

  map.parkArea = null;
  return true;
}

export function findParkAreaAt(map: MapDocument, point: Vec2): ParkArea | undefined {
  if (!map.parkArea) {
    return undefined;
  }

  return pointInPolygon(point, tessellateRing(map.parkArea)) ? map.parkArea : undefined;
}

/** Inside the park polygon and not on solid land. */
export function isInsideParkArea(map: MapDocument, point: Vec2): boolean {
  return findParkAreaAt(map, point) !== undefined && !pointInSolid(map, point);
}

export function findParkAreaVertexAt(
  map: MapDocument,
  point: Vec2,
  radius: number,
): number | null {
  if (!map.parkArea) {
    return null;
  }

  const radiusSq = radius * radius;
  let best: { index: number; dist: number } | null = null;

  for (let index = 0; index < map.parkArea.vertices.length; index += 1) {
    const vertex = map.parkArea.vertices[index];

    if (!vertex) {
      continue;
    }

    const dist = distanceSquared(point, vertex);

    if (dist <= radiusSq && (!best || dist < best.dist)) {
      best = { index, dist };
    }
  }

  return best?.index ?? null;
}

export function moveParkAreaVertex(map: MapDocument, index: number, point: Vec2): boolean {
  if (!map.parkArea) {
    return false;
  }

  return moveRingVertex(map.parkArea, index, clampToBounds(point, map.bounds));
}

export function removeParkAreaVertex(map: MapDocument, index: number): boolean {
  if (!map.parkArea || map.parkArea.vertices[index] === undefined) {
    return false;
  }

  if (map.parkArea.vertices.length <= MIN_RING_VERTICES) {
    return false;
  }

  return removeRingVertex(map.parkArea, index);
}

export function setParkAreaCornerRadius(map: MapDocument, radius: number): boolean {
  if (!map.parkArea) {
    return false;
  }

  setCornerRadius(map.parkArea, radius);
  return true;
}

export function setParkAreaColor(map: MapDocument, color: Rgba): boolean {
  if (!map.parkArea) {
    return false;
  }

  map.parkArea.color = cloneRgba(color);
  return true;
}

export function findParkAreaEdgeAt(map: MapDocument, point: Vec2, radius: number): number | null {
  return map.parkArea ? findRingEdgeAt(map.parkArea, point, radius) : null;
}

export function findParkAreaBendAt(map: MapDocument, point: Vec2, radius: number): number | null {
  return map.parkArea ? findBendHandleAt(map.parkArea, point, radius) : null;
}

export function setParkAreaEdgeBend(map: MapDocument, edgeIndex: number, point: Vec2): boolean {
  return map.parkArea ? setEdgeBend(map.parkArea, edgeIndex, clampToBounds(point, map.bounds)) : false;
}

export function moveParkAreaBend(map: MapDocument, edgeIndex: number, point: Vec2): boolean {
  return map.parkArea
    ? moveBendHandle(map.parkArea, edgeIndex, clampToBounds(point, map.bounds))
    : false;
}

export function clearParkAreaEdgeBend(map: MapDocument, edgeIndex: number): boolean {
  return map.parkArea ? clearEdgeBend(map.parkArea, edgeIndex) : false;
}
